import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import ImmutableMap from 'util/ImmutableMap';
import {JSONLeafValue, JSONValue} from 'util/Types';

/**
 * A filter operation, as it is phrased to the user.
 *
 * @package
 */
export enum UserFacingFilterOperation {
  // Identity operations
  EQUALS = 'equals',
  DOES_NOT_EQUAL = 'does not equal',
  IS_NULL = 'is null',
  IS_NOT_NULL = 'is not null',
  // Greater/less than operations
  IS_LESS_THAN = 'is less than',
  IS_MORE_THAN = 'is more than',
  IS_AT_MOST = 'is at most',
  IS_AT_LEAST = 'is at least',
  // Contains/includes operations
  CONTAINS = 'contains',
  DOES_NOT_CONTAIN = 'does not contain',
  IS_ONE_OF = 'is one of',
  IS_NONE_OF = 'is none of',
  INCLUDES = 'includes',
  DOES_NOT_INCLUDE = 'does not include',
  // Text operations
  STARTS_WITH = 'starts with',
  DOES_NOT_START_WITH = 'does not start with',
  ENDS_WITH = 'ends with',
  DOES_NOT_END_WITH = 'does not end with',
}

/**
 * A filter, defined in terms of how it is presented to the user, rather than in terms of how it is persisted.
 *
 * @package
 */
export interface UserFacingFilter {
  columnSystemName: string;
  operator: UserFacingFilterOperation;
  value: JSONValue;
}

/**
 * Operators for which the filter value is expected to be a list (not necessarily that the filter column
 * is a list-type column)
 */
const LIST_FILTER_OPERATORS: Array<UserFacingFilterOperation> = [
  UserFacingFilterOperation.IS_ONE_OF,
  UserFacingFilterOperation.IS_NONE_OF,
];

export function isListValueOperator(operator: UserFacingFilterOperation): boolean {
  return LIST_FILTER_OPERATORS.includes(operator);
}

/**
 * Filter operations, as persisted by the FILTER_DATASET step.
 */
export enum DataFrameFilterOperation {
  AND = 'AND',
  CONTAINS = 'CONTAINS',
  ENDS_WITH = 'ENDS_WITH',
  EQUALS = 'EQUALS',
  GREATER_THAN = 'GREATER_THAN',
  GREATER_THAN_OR_EQUALS = 'GREATER_THAN_OR_EQUALS',
  INCLUDES = 'INCLUDES',
  IS_IN = 'IS_IN',
  IS_NULL = 'IS_NULL',
  LESS_THAN = 'LESS_THAN',
  LESS_THAN_OR_EQUALS = 'LESS_THAN_OR_EQUALS',
  OR = 'OR',
  STARTS_WITH = 'STARTS_WITH',
}

export type DataFrameFilterConjunction = DataFrameFilterOperation.AND | DataFrameFilterOperation.OR;

export const CONJUNCTION_TO_READABLE: {[key in DataFrameFilterConjunction]: string} = {
  [DataFrameFilterOperation.AND]: 'And',
  [DataFrameFilterOperation.OR]: 'Or',
};

export const OPERATOR_EQUIVALENCES: ImmutableMap<
  UserFacingFilterOperation,
  Pick<DataFrameFilterCondition, 'operation' | 'not_'>
> = new ImmutableMap([
  // Identity operations
  [UserFacingFilterOperation.IS_NULL, {operation: DataFrameFilterOperation.IS_NULL, not_: false}],
  [UserFacingFilterOperation.IS_NOT_NULL, {operation: DataFrameFilterOperation.IS_NULL, not_: true}],
  [UserFacingFilterOperation.EQUALS, {operation: DataFrameFilterOperation.EQUALS, not_: false}],
  [UserFacingFilterOperation.DOES_NOT_EQUAL, {operation: DataFrameFilterOperation.EQUALS, not_: true}],
  // Greater/less than operations
  [UserFacingFilterOperation.IS_LESS_THAN, {operation: DataFrameFilterOperation.LESS_THAN, not_: false}],
  [UserFacingFilterOperation.IS_MORE_THAN, {operation: DataFrameFilterOperation.GREATER_THAN, not_: false}],
  [
    UserFacingFilterOperation.IS_AT_MOST,
    {operation: DataFrameFilterOperation.LESS_THAN_OR_EQUALS, not_: false},
  ],
  [
    UserFacingFilterOperation.IS_AT_LEAST,
    {operation: DataFrameFilterOperation.GREATER_THAN_OR_EQUALS, not_: false},
  ],
  // Contains/includes operations
  [UserFacingFilterOperation.IS_ONE_OF, {operation: DataFrameFilterOperation.IS_IN, not_: false}],
  [UserFacingFilterOperation.IS_NONE_OF, {operation: DataFrameFilterOperation.IS_IN, not_: true}],
  [UserFacingFilterOperation.INCLUDES, {operation: DataFrameFilterOperation.INCLUDES, not_: false}],
  [UserFacingFilterOperation.DOES_NOT_INCLUDE, {operation: DataFrameFilterOperation.INCLUDES, not_: true}],
  // Text operations
  [UserFacingFilterOperation.CONTAINS, {operation: DataFrameFilterOperation.CONTAINS, not_: false}],
  [UserFacingFilterOperation.DOES_NOT_CONTAIN, {operation: DataFrameFilterOperation.CONTAINS, not_: true}],
  [UserFacingFilterOperation.STARTS_WITH, {operation: DataFrameFilterOperation.STARTS_WITH, not_: false}],
  [
    UserFacingFilterOperation.DOES_NOT_START_WITH,
    {operation: DataFrameFilterOperation.STARTS_WITH, not_: true},
  ],
  [UserFacingFilterOperation.ENDS_WITH, {operation: DataFrameFilterOperation.ENDS_WITH, not_: false}],
  [UserFacingFilterOperation.DOES_NOT_END_WITH, {operation: DataFrameFilterOperation.ENDS_WITH, not_: true}],
]);

interface BaseDataFrameFilterCondition<
  TOperation extends Exclude<DataFrameFilterOperation, DataFrameFilterConjunction>,
> {
  operation: TOperation;
  columnSystemName: string;
  not_: boolean;
}

export type DataFrameFilterCondition =
  | BaseDataFrameFilterCondition<DataFrameFilterOperation.IS_NULL>
  | (BaseDataFrameFilterCondition<DataFrameFilterOperation.IS_IN> & {value: Array<JSONValue>})
  | (BaseDataFrameFilterCondition<
      Exclude<
        DataFrameFilterOperation,
        | DataFrameFilterOperation.IS_NULL
        | DataFrameFilterOperation.IS_IN
        | DataFrameFilterOperation.AND
        | DataFrameFilterOperation.OR
      >
    > & {value: JSONLeafValue});

/**
 * A filter, which may be composed of one or more condition.
 *
 * Currently, only one filter may be added per column.
 */
export interface DataFrameFilter {
  operation: DataFrameFilterConjunction;
  filters: Array<DataFrameFilterCondition>;
}

/**
 * Step config for a filter step, including all filters applied to the data frame.
 *
 * Currently, only AND logic is supported at the top level.
 */
export interface DataFrameFilterStepConfig {
  filter: {
    operation: DataFrameFilterOperation.AND;
    filters: Array<DataFrameFilter>;
  };
}

/**
 * Check that a FILTER_DATASET step's `filter` matches the regularlized form expected by this module:
 * - a top-level 'AND' operation, containing
 *    - zero-or-more 'AND' or 'OR' operations, each of which contains
 *      - zero-or-more filter conditions for the same column
 */
export function isFilterStepConfigVariableRegularShape(
  filter: PipelineStepConfig<PipelineStepType.FILTER_DATASET, 0>['filter']
): filter is DataFrameFilterStepConfig['filter'] {
  return (
    // There is a top-level conjunction...
    filter.operation === DataFrameFilterOperation.AND &&
    // ...of conjunctions and disjunctions...
    filter.filters.every(
      (filter) =>
        filter.operation === DataFrameFilterOperation.AND || filter.operation === DataFrameFilterOperation.OR
    ) &&
    // ...of column-specific conditions.
    filter.filters.every(
      (filter) => new Set(filter.filters.map(({columnSystemName}) => columnSystemName)).size < 2
    )
  );
}
